import { Checkbox, Col, Form, Input, InputNumber } from 'antd'
import DescriptionFormItem from 'components/CIMSFormItem/common/DescriptionFormItem'
import NameFormItem from 'components/CIMSFormItem/common/NameFormItem'
import { alphabeticalSort, listStringify } from 'shared'
import { HelpMessage } from 'utils/constants'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSFormItemList from 'components/CIMSFormItemList'
import { useTargetsContext } from '../../TargetsContext'
import { CreateEditFormItemsValues } from './types'

const CreateEditFormItems: React.VFC<{ showRequired?: true }> = ({
  showRequired,
}) => {
  const { metadata } = useTargetsContext()
  const help = HelpMessage.Materials.Asphalt
  return (
    <>
      <NameFormItem help={HelpMessage.Name} />
      <DescriptionFormItem entityName="Target" />
      {showRequired && (
        <CIMSFormItem
          label="Required"
          name="required"
          valuePropName="checked"
          initialValue={true}
          help={help.Required}
        >
          <Checkbox />
        </CIMSFormItem>
      )}
      <CIMSFormItemList
        name="percentages"
        help={help.MixDesignPercentages}
        label="Mix Design"
        buttons={[
          {
            type: 'dropdown',
            icon: 'add',
            items: metadata.mixDesigns
              .map(({ globalId, mixDesignNumber }) => ({
                label: mixDesignNumber ?? 'Unknown',
                key: globalId,
              }))
              .sort(alphabeticalSort('label')),
            loading: metadata.loading,
            add: (key) => {
              const found = metadata.mixDesigns.find(
                ({ globalId }) => globalId === key
              )
              if (!found) return undefined
              return {
                mixDesignId: found.globalId,
                mixDesignNumber: found.mixDesignNumber,
                percentage: null,
              }
            },
          },
        ]}
        validate={(value: CreateEditFormItemsValues['percentages']) => {
          if (!value) return true
          const numNums = value.reduce(
            (map, { mixDesignNumber: num }) =>
              // Mix design number will be set before this code is reached, so using bang
              map.set(num!, [...(map.get(num!) ?? []), num!]),
            new Map<string, string[]>()
          )
          const duplicates = [...numNums.entries()].reduce(
            (names, [key, values]) => {
              if (values.length > 1) names.push(key)
              return names
            },
            Array<string>()
          )
          if (duplicates.length > 0) {
            const numbers = listStringify({ items: duplicates })
            return `Duplicate Mix Designs are not allowed. Please remove extra lines for: ${numbers}`
          }
          return true
        }}
      >
        {(name) => (
          <>
            <Col flex={3}>
              <Form.Item hidden name={[name, 'mixDesignId']}>
                <Input />
              </Form.Item>
              <CIMSFormItem
                help={help.MixID}
                label="Mix Design Number"
                name={[name, 'mixDesignNumber']}
              >
                <Input disabled />
              </CIMSFormItem>
            </Col>
            <Col flex={1}>
              <CIMSFormItem
                help={help.Percentage}
                label="Percentage"
                name={[name, 'percentage']}
                requiredMessage="Please enter a percentage"
                customValidator={{
                  message: 'Please enter a value between 0 and 100',
                  validator: (value) => value >= 0 && value <= 100,
                }}
              >
                <InputNumber style={{ width: '100%' }} />
              </CIMSFormItem>
            </Col>
          </>
        )}
      </CIMSFormItemList>
    </>
  )
}

export default CreateEditFormItems
